import { useState, useEffect } from 'react';
import { ContentProvider } from '@/types/knowledge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface ContentProviderEditDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (providerData: Partial<ContentProvider>) => void;
    contentProvider: ContentProvider | null;
}

export const ContentProviderEditDialog = ({
    isOpen,
    onClose,
    onSave,
    contentProvider,
}: ContentProviderEditDialogProps) => {
    const [name, setName] = useState('');

    useEffect(() => {
        if (contentProvider) {
            setName(contentProvider.name || '');
        } else {
            setName('');
        }
    }, [contentProvider, isOpen]);

    const handleSubmit = () => {
        if (!name.trim()) {
            return;
        }
        onSave({ name });
        onClose();
    };

    const getInputStyles = () => ({
        borderColor: "hsl(var(--muted-foreground))",
    });

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.style.borderColor = "hsl(var(--primary))";
        e.target.style.boxShadow = "0 0 0 2px hsl(var(--primary) / 0.2)";
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.style.borderColor = "hsl(var(--muted-foreground))";
        e.target.style.boxShadow = 'none';
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="bg-background">
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        {contentProvider ? 'Edit Content Provider' : 'Create Content Provider'}
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {contentProvider
                            ? 'Update the content provider name.'
                            : 'Create a new content provider. Only the name can be edited.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    <div>
                        <label
                            className="block text-sm font-medium mb-1 text-foreground"
                        >
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                            style={getInputStyles()}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter content provider name"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border rounded-md border-border text-foreground hover:bg-muted transition-colors bg-background"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!name.trim()}
                        className="px-4 py-2 rounded-md transition-colors bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {contentProvider ? 'Update' : 'Create'}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
